import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { datadogLogs } from '@datadog/browser-logs';
import { useCompleteOnboardingFormMutation, adminsApi } from 'services/adminsApi';
import { useAppDispatch } from 'helpers/hooks';
import { trackEvent } from 'helpers/mixpanelEvents';
import { useAuth0Config } from 'hooks/useAuth0Config';
import { Stepper } from 'components/Stepper';
import { showToast } from 'components/Toast';
import { HUBSPOT_FORM } from 'config/hubspot';
import { ENV_CONFIG } from 'config/environment';
import { FormSubmitted } from './FormSubmitted';

interface OnboardingFormProps {
  recipientId?: string;
}

/**
 * Onboarding form page powered by HubSpot.
 * Shown to primary contacts who have accepted TOS but haven't completed the onboarding form.
 */
export const OnboardingForm = ({ recipientId }: OnboardingFormProps) => {
  const formRef = useRef<HTMLDivElement>(null);
  const isSavingRef = useRef(false);
  const dispatch = useAppDispatch();
  const { handleAccessToken } = useAuth0Config();
  const handleAccessTokenRef = useRef(handleAccessToken);
  const [completeOnboardingForm] = useCompleteOnboardingFormMutation();
  const [activeStep, setActiveStep] = useState(1);
  const [isFormReady, setIsFormReady] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [hasSubmissionError, setHasSubmissionError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const supportEmail = ENV_CONFIG().RECIPIENTS_SUPPORT_EMAIL;

  const saveOnboardingCompletion = useCallback(() => {
    if (!recipientId) {
      datadogLogs.logger.error('Onboarding completion called without recipientId');
      return;
    }
    if (isSavingRef.current) return;
    isSavingRef.current = true;
    setIsSubmitting(true);
    handleAccessTokenRef
      .current()
      .then(token => {
        // undefined means handleAccessToken already triggered loginWithRedirect
        // (refresh token idle-expired); let that redirect take over.
        // Don't clear hasSubmissionError here — that unmounts the retry, which is
        // the only recovery left if the redirect never navigates.
        if (!token) return;
        return completeOnboardingForm({ recipientId })
          .unwrap()
          .then(() => {
            trackEvent('Onboarding Form Submitted', { screen: 'recipients_dashboard' });
            setIsSubmitted(true);
          });
      })
      .catch(error => {
        datadogLogs.logger.error(
          'Error occurred saving Recipient Onboarding status from Hubspot',
          {},
          error
        );
        setHasSubmissionError(true);
        showToast('Failed to save onboarding status. Please try again.', 'error');
      })
      .finally(() => {
        isSavingRef.current = false;
        setIsSubmitting(false);
      });
  }, [recipientId, completeOnboardingForm]);

  useEffect(() => {
    handleAccessTokenRef.current = handleAccessToken;
  }, [handleAccessToken]);

  const handleContinue = useCallback(() => {
    dispatch(adminsApi.util.invalidateTags(['OBCheck']));
  }, [dispatch]);

  // Listen for HubSpot form ready event before the embed script loads.
  // Must be registered before the script executes to avoid race conditions.
  // See: https://developers.hubspot.com/docs/api-reference/global-form-events/guide
  useEffect(() => {
    const handleFormReady = (event: Event) => {
      const detail = (event as CustomEvent<{ formId: string }>).detail;
      if (detail?.formId === HUBSPOT_FORM.FORMS.RECIPIENT_ONBOARDING) {
        setIsFormReady(true);
      }
    };
    window.addEventListener(HUBSPOT_FORM.EVENTS.ON_READY, handleFormReady);
    return () => {
      window.removeEventListener(HUBSPOT_FORM.EVENTS.ON_READY, handleFormReady);
    };
  }, []);

  // Load the HubSpot embed script (singleton; not removed on unmount).
  useEffect(() => {
    const existingScript = document.getElementById(HUBSPOT_FORM.SCRIPT_ID);
    if (!existingScript) {
      const script = document.createElement('script');
      script.id = HUBSPOT_FORM.SCRIPT_ID;
      script.src = HUBSPOT_FORM.SCRIPT_SRC;
      script.defer = true;
      document.body.appendChild(script);
    }
  }, []);

  // Attach form interaction listeners.
  useEffect(() => {
    const el = formRef.current;
    if (!el) return;

    const handleNavigateNext = () => {
      setActiveStep(prev => Math.min(prev + 1, HUBSPOT_FORM.TOTAL_STEPS));
    };

    const handleNavigatePrevious = () => {
      setActiveStep(prev => Math.max(prev - 1, 1));
    };

    el.addEventListener(HUBSPOT_FORM.EVENTS.ON_SUBMISSION_SUCCESS, saveOnboardingCompletion);
    el.addEventListener(HUBSPOT_FORM.EVENTS.ON_NAVIGATE_NEXT, handleNavigateNext);
    el.addEventListener(HUBSPOT_FORM.EVENTS.ON_NAVIGATE_PREVIOUS, handleNavigatePrevious);
    return () => {
      el.removeEventListener(HUBSPOT_FORM.EVENTS.ON_SUBMISSION_SUCCESS, saveOnboardingCompletion);
      el.removeEventListener(HUBSPOT_FORM.EVENTS.ON_NAVIGATE_NEXT, handleNavigateNext);
      el.removeEventListener(HUBSPOT_FORM.EVENTS.ON_NAVIGATE_PREVIOUS, handleNavigatePrevious);
    };
  }, [saveOnboardingCompletion]);

  if (isSubmitted) {
    return <FormSubmitted onContinue={handleContinue} />;
  }

  return (
    <div className="onboarding-form">
      <div className="onboarding-form__gradient" />
      <div className="onboarding-form__body">
        <div className="onboarding-form__header">
          <h1>Recipient Onboarding Form</h1>
          <p>
            Please complete this form to share more details about your entity, as well as primary
            contact information. Note: Progress will not be saved. The form must be filled out
            completely in order for it to be properly submitted.{' '}
            <strong>Submission of this form does not guarantee donations.</strong> Please reach out
            to{' '}
            <Link to={`mailto:${supportEmail}`} target="_blank" rel="noopener noreferrer">
              {supportEmail}
            </Link>{' '}
            with any questions!
          </p>
          <Stepper totalSteps={HUBSPOT_FORM.TOTAL_STEPS} activeStep={activeStep} />
        </div>
        <div className="onboarding-form__form">
          <div
            ref={formRef}
            className="hs-form-frame"
            data-region={HUBSPOT_FORM.REGION}
            data-form-id={HUBSPOT_FORM.FORMS.RECIPIENT_ONBOARDING}
            data-portal-id={HUBSPOT_FORM.PORTAL_ID}
          />
          <div
            className={`onboarding-form__skeleton${isFormReady ? ' onboarding-form__skeleton--hidden' : ''}`}
            aria-hidden={isFormReady}
          />
        </div>
        {hasSubmissionError && (
          <div className="onboarding-form__error">
            {!isSubmitting && (
              <p>
                Something went wrong completing your setup. Please try again or contact{' '}
                <Link to={`mailto:${supportEmail}`} target="_blank" rel="noopener noreferrer">
                  {supportEmail}
                </Link>
                .
              </p>
            )}
            <p className="sr-only" role="status" aria-live="polite">
              {isSubmitting ? 'Saving your setup\u2026' : ''}
            </p>
            <button
              className="onboarding-form__retry"
              onClick={saveOnboardingCompletion}
              disabled={isSubmitting}
              type="button"
            >
              {isSubmitting ? 'Saving\u2026' : 'Try Again'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
